import { useEffect, useState } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Printer, ShieldAlert } from "lucide-react";
import PrescriptionPDF from "@/components/PrescriptionPDF";
import ConsultationBriefPanel from "@/components/ConsultationBriefPanel";
import { ConsultationMedicinesSection } from "@/components/ConsultationMedicinesSection";
import { ConsultationInvestigationsSection } from "@/components/ConsultationInvestigationsSection";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";

const EMPTY_MEDICINE = { name: "", strength: "", dosage: "", frequency: "", food_timing: "", duration: "", instructions: "" };

// The full "AI Consultation Assistant + Digital Prescription" experience, extracted
// out of ConsultationPage so it can be reused both as its own routed page (opened
// from the Appointments tab's "Consult" button) AND embedded directly inside the
// Patient Profile's "AI Consultation & Digital Prescription" tab for whichever
// appointment is currently "With Doctor".
export default function ConsultationContent({ patientId, appointmentId, patient: patientProp, onSaved }) {
  const isReadOnly = useIsGracePeriod();
  const [patient, setPatient] = useState(patientProp || null);
  const [medicines, setMedicines] = useState([{ ...EMPTY_MEDICINE, _key: crypto.randomUUID() }]);
  const [dietAdvice, setDietAdvice] = useState("");
  const [exerciseAdvice, setExerciseAdvice] = useState("");
  const [lifestyleAdvice, setLifestyleAdvice] = useState("");
  const [investigations, setInvestigations] = useState([]);
  const [customInvestigation, setCustomInvestigation] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupNotes, setFollowupNotes] = useState("");
  const [weight, setWeight] = useState("");
  const [bp, setBp] = useState("");
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);

  useEffect(() => {
    if (patientProp) {
      setPatient(patientProp);
      return;
    }
    api.get(`/patients/${patientId}`).then(({ data }) => setPatient(data));
  }, [patientId, patientProp]);

  useEffect(() => {
    const loadBrief = async () => {
      setBriefLoading(true);
      try {
        const { data } = await api.get(`/ai/appointments/${appointmentId}/brief`);
        if (data) {
          setBrief(data);
          applyBriefVitals(data);
        } else {
          const { data: generated } = await api.post(`/ai/appointments/${appointmentId}/brief`);
          setBrief(generated);
          applyBriefVitals(generated);
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to generate AI consultation brief");
      } finally {
        setBriefLoading(false);
      }
    };
    loadBrief();
  }, [appointmentId]);

  const applyBriefVitals = (data) => {
    if (data?.latest_weight) setWeight((w) => w || data.latest_weight);
    if (data?.latest_bp) setBp((b) => b || data.latest_bp);
  };

  const regenerateBrief = async () => {
    setBriefLoading(true);
    try {
      const { data } = await api.post(`/ai/appointments/${appointmentId}/brief`);
      setBrief(data);
      applyBriefVitals(data);
      toast.success("Patient Brief regenerated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to regenerate brief");
    } finally {
      setBriefLoading(false);
    }
  };

  const toggleInvestigation = (name) => {
    setInvestigations((inv) => (inv.includes(name) ? inv.filter((i) => i !== name) : [...inv, name]));
  };

  const savePrescription = async () => {
    const customInvestigations = customInvestigation
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      const { data } = await api.post("/prescriptions", {
        appointment_id: appointmentId,
        patient_id: patientId,
        medicines: medicines.filter((m) => m.name),
        diet_advice: dietAdvice,
        exercise_advice: exerciseAdvice,
        lifestyle_advice: lifestyleAdvice,
        investigations: [...investigations, ...customInvestigations],
        followup_date: followupDate || null,
        followup_notes: followupNotes,
        clinical_weight: weight,
        clinical_bp: bp,
        clinical_notes: notes,
      });
      setSaved(data);
      toast.success("Prescription saved. Consultation completed.");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!patient) return <p className="text-slate-500">Loading consultation...</p>;

  return (
    <>
      <ConsultationBriefPanel brief={brief} briefLoading={briefLoading} regenerateBrief={regenerateBrief} />

      <div className="mt-6 card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <h2 className="font-semibold text-slate-900">Clinical Examination</h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div><Label>Weight (kg)</Label><Input data-testid="consult-weight" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
          <div><Label>Blood Pressure (mm Hg)</Label><Input data-testid="consult-bp" value={bp} onChange={(e) => setBp(e.target.value)} /></div>
          <div className="sm:col-span-1"><Label>General Notes</Label><Input data-testid="consult-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
      </div>

      <ConsultationMedicinesSection medicines={medicines} setMedicines={setMedicines} EMPTY_MEDICINE={EMPTY_MEDICINE} />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <h2 className="font-semibold text-slate-900">Lifestyle Advice</h2>
          <div className="mt-3 space-y-3">
            <div><Label>Diet</Label><Textarea data-testid="consult-diet" value={dietAdvice} onChange={(e) => setDietAdvice(e.target.value)} /></div>
            <div><Label>Exercise</Label><Textarea data-testid="consult-exercise" value={exerciseAdvice} onChange={(e) => setExerciseAdvice(e.target.value)} /></div>
            <div><Label>Lifestyle Instructions</Label><Textarea data-testid="consult-lifestyle" value={lifestyleAdvice} onChange={(e) => setLifestyleAdvice(e.target.value)} /></div>
          </div>
        </div>

        <ConsultationInvestigationsSection
          investigations={investigations}
          toggleInvestigation={toggleInvestigation}
          customInvestigation={customInvestigation}
          setCustomInvestigation={setCustomInvestigation}
          followupDate={followupDate}
          setFollowupDate={setFollowupDate}
          followupNotes={followupNotes}
          setFollowupNotes={setFollowupNotes}
        />
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button data-testid="save-prescription-btn" className="gap-2 bg-teal-700 hover:bg-teal-800" disabled={isReadOnly} title={isReadOnly ? "Disabled during subscription grace period" : undefined} onClick={savePrescription}>
          <Save className="h-4 w-4" /> Save Prescription
        </Button>
        {saved && (
          <PDFDownloadLink document={<PrescriptionPDF patient={patient} prescription={saved} />} fileName={`${patient.patient_code}_prescription.pdf`}>
            {({ loading }) => (
              <Button variant="outline" data-testid="print-prescription-btn" className="gap-2 border-teal-700 text-teal-700">
                <Printer className="h-4 w-4" /> {loading ? "Preparing PDF..." : "Print Prescription"}
              </Button>
            )}
          </PDFDownloadLink>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        The AI is an assistant only. It never diagnoses, recommends medicines or dosages, or replaces clinical judgment. All treatment decisions remain the responsibility of the doctor.
      </div>
    </>
  );
}
